import { ROUTES_PATH } from 'constants/routesPath'
import { useRemoteConfig } from 'modules/RemoteConfig/hooks/useRemoteConfig'
import { useCallback, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { goToPage } from 'redesign/utils/goToPage'
import { useIsSafeURL } from 'redesign/hooks/useIsSafeURL'
import { getQueryParamsValue } from 'utils'

interface redirectUrlProps {
    redirectToUrl: string
    target?: '_self' | '_blank'
    redirectParams?: string
}

export const useRedirectTo = () => {
    const { isFeatureEnabled } = useRemoteConfig()
    const newRedirectFlow = isFeatureEnabled('AUTH_NEW_REDIRECT_FLOW')
    const location = useLocation()
    const { isSafeURL } = useIsSafeURL()

    const redirectToParam: string | null = getQueryParamsValue(location, 'redirectTo') || null
    const deleteRedirectParam = useCallback((): string => {
        const params = new URLSearchParams(location.search)
        params.delete('redirectTo')
        return params.toString()
    }, [location.search])

    const redirectTo = useMemo(() => {
        if (redirectToParam === null) {
            return
        }

        let verifyUrl = redirectToParam.startsWith('/') ? `${redirectToParam}` : `/${redirectToParam}`

        const params = deleteRedirectParam()

        if (params) {
            const char = verifyUrl.includes('?') ? '&' : '?'
            verifyUrl = `${verifyUrl}${char}${params}`
        }

        return verifyUrl
    }, [deleteRedirectParam, redirectToParam])

    function verifyRedirectUrl({ redirectToUrl, target = '_self', redirectParams = '' }: redirectUrlProps) {
        if (newRedirectFlow) {
            if (redirectParams) {
                return goToPage(`${ROUTES_PATH.FORWARDING_APP}${redirectParams}`)
            }

            const params = new URLSearchParams(location.search)
            if (redirectToUrl) {
                params.delete('redirectTo')
                params.append('redirectTo', redirectToUrl)
            }

            params.delete('phased-login-origin')
            params.delete('u')
            location.search = `?${params.toString()}`

            return goToPage(`${ROUTES_PATH.FORWARDING_APP}${location.search}`)
        }

        const isExternalURL = redirectToUrl.startsWith('http')

        if (isExternalURL) {
            if (isSafeURL(redirectToUrl)) {
                window.open(redirectToUrl, target)
            } else {
                console.error('Tentativa de redirecionamento para domínio não permitido:', redirectToUrl)
            }
            return
        }
        // TODO: REFACTOR
        const params = sessionStorage.getItem('@LiviaAuth:Campaigns') ? sessionStorage.getItem('@LiviaAuth:Campaigns') : ''
        const verifyUrl = redirectToUrl.startsWith('/') ? `${redirectToUrl}` : `/${redirectToUrl}`

        const newArchitecture = verifyUrl.startsWith('/plataforma') ? `${redirectToUrl}` : `${params}`

        goToPage(newArchitecture)

        sessionStorage.removeItem('@LiviaAuth:Campaigns')

        sessionStorage.removeItem('EXTERNAL_COMPANY_DATA')
        return
    }

    const redirectToPath = useCallback(
        ({
            defaultRedirect = ROUTES_PATH.FORWARDING_APP,
            redirectToActions,
            defaultRedirectActions,
        }: {
            defaultRedirect?: string
            redirectToActions?: () => void
            defaultRedirectActions?: () => void
        }) => {
            if (redirectTo) {
                window.history.pushState({}, '', redirectTo)

                return redirectToActions && redirectToActions()
            }

            if (defaultRedirect) {
                window.history.pushState({}, '', defaultRedirect)

                return defaultRedirectActions && defaultRedirectActions()
            }
        },
        [redirectTo],
    )

    return {
        redirectTo,
        newRedirectFlow,
        redirectToPath,
        verifyRedirectUrl,
    }
}
